import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useAuthInfo } from '../lib/jwt';
import { EntityType } from '../types/dto/EntityType';
import { ReactionType } from '../types/dto/ReactionType';

type Entity = {
  id: number,
  userReactions?: ReactionType[],
  likesCount?: number,
  reactionsCount?: number,
  reactionsCountByReaction?: {
    [key: number]: number
  }
};

type ReactionHandlerArgs<T extends  Entity> = {
  entity: T,
  type: EntityType,
  isComment?: boolean,
  linkedEntityId?: number,
  onChanged: (entity: T) => void
};

export function useReactionHandler<T extends Entity>({
  entity,
  type,
  isComment,
  linkedEntityId,
  onChanged
}: ReactionHandlerArgs<T>) {
  const [authInfo, token] = useAuthInfo();
  const router = useRouter();

  const setReactionHandler = useCallback(async (reaction?: number) => {
    try {
      if (!token) {
        router.push('/login');
        return;
      }

      if (typeof reaction !== 'number') {
        reaction = 1;
      }

      const isLike = reaction === 1;
      const isReactedByUser = entity.userReactions?.some(t => t.reaction === reaction);
      const newEntity = { ...entity };

      if (isReactedByUser) {
        newEntity.userReactions = entity.userReactions.filter(t => t.reaction !== reaction);

        if (!isLike) {
          newEntity.reactionsCount -= 1;
        }

        if (isLike && !isComment) {
          newEntity.likesCount -= 1;
        }

        if (isComment) {
          newEntity.reactionsCount -= 1;
        }

        if (!isLike && entity.reactionsCountByReaction) {
          entity.reactionsCountByReaction[reaction] -= 1;
        }
      }
      else {
        newEntity.userReactions = [
          ...(entity.userReactions || []),
          { reaction: reaction }
        ];

        if (!isLike) {
          newEntity.reactionsCount = (newEntity.reactionsCount || 0) + 1;
        }

        if (isLike && !isComment) {
          newEntity.likesCount = (newEntity.likesCount || 0) + 1;
        }

        if (isComment) {
          newEntity.reactionsCount = (newEntity.reactionsCount || 0) + 1;
        }

        if (!isLike && entity.reactionsCountByReaction) {
          entity.reactionsCountByReaction[reaction] = (entity.reactionsCountByReaction[reaction] || 0) + 1;
        }
      }

      onChanged(newEntity);

      await fetch(`/api/reactions/${type}`, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          linkedEntityId: isComment ? linkedEntityId : entity.id,
          commentId: isComment ? entity.id : undefined,
          reaction: reaction,
          userId: authInfo.id
        })
      });
    }
    catch (error) {
      console.error(error);
    }
  }, [
    token,
    authInfo?.id,
    entity,
    onChanged
  ]);

  return setReactionHandler;
}
